import { motion } from "framer-motion";
import { Phone, Clock, Zap } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SubmissionForm from "@/components/SubmissionForm";
import { overline } from "@/components/kit";
import { CITY_DATA, CITY_IMAGES } from "@/data/cities";
import { CONTACT } from "@/data/site";
import { CityProjects, CityCTA } from "@/components/CityShared";
import CityLocalBusinessJsonLd from "@/components/CityLocalBusinessJsonLd";

// PARIS — Multi-column editorial layout with prominent phone (decision-maker friendly)
export default function CityParis() {
  const c = CITY_DATA.paris;
  return (
    <>
      <Seo title={`Événementiel à ${c.name}`} description={c.seoDesc} keywords={c.seoKeywords} path="/villes/paris" />
      <CityLocalBusinessJsonLd city={c} />

      {/* Sticky phone-first CTA for Paris decision-makers */}
      <div className="fixed bottom-4 left-1/2 z-40 -translate-x-1/2 md:bottom-6" data-testid="paris-sticky-phone">
        <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} className="flex items-center gap-3 rounded-full bg-ink px-5 py-3.5 text-white shadow-2xl shadow-ink/25 transition-all hover:scale-105 hover:bg-lime-dim hover:text-ink">
          <Phone size={18} />
          <span className="text-sm font-medium">Appeler Paris — <strong>{CONTACT.phoneMobile}</strong></span>
        </a>
      </div>

      <section className="border-b border-ink/10 bg-mist pt-36 pb-16 md:pt-44 md:pb-24">
        <div className="mx-auto grid max-w-[1440px] items-end gap-10 px-5 md:grid-cols-12 md:px-10">
          <div className="md:col-span-7">
            <Reveal><span className={overline}>{c.region}</span></Reveal>
            <Reveal delay={1}><h1 className="mt-6 font-serif text-6xl font-medium leading-[0.9] tracking-tight text-ink md:text-8xl lg:text-9xl">Événementiel<br />à <span className="italic text-lime-dim">{c.name}</span></h1></Reveal>
            <Reveal delay={2}><p className="mt-8 max-w-xl text-lg leading-relaxed text-ink/60">{c.intro}</p></Reveal>

            {/* Prominent phone block — decision-makers want direct contact */}
            <Reveal delay={3}>
              <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} className="mt-10 inline-flex items-center gap-4 rounded-2xl bg-ink px-6 py-5 text-white transition-all hover:scale-[1.02] hover:bg-lime-dim hover:text-ink" data-testid="paris-hero-phone">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-lime text-ink"><Phone size={20} /></span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] opacity-60">Première réponse sous 24 h ouvrées</div>
                  <div className="mt-0.5 font-serif text-3xl">{CONTACT.phoneMobile}</div>
                </div>
              </a>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 gap-4 md:col-span-5">
            {CITY_IMAGES.paris.map((img, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.15, duration: 0.8 }} className={`overflow-hidden rounded-2xl ${i === 1 ? "mt-10" : ""}`}>
                <img src={img} alt={`${c.name} ${i}`} className="h-64 w-full object-cover md:h-80" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Réactivité promise — Paris-specific */}
      <section className="border-b border-ink/10 bg-white py-12" data-testid="paris-promise">
        <div className="mx-auto grid max-w-[1440px] gap-6 px-5 md:grid-cols-3 md:px-10">
          <div className="flex items-start gap-4">
            <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-lime text-ink"><Zap size={18} /></span>
            <div>
              <div className="font-serif text-xl text-ink">Réponse sous 24 h ouvrées</div>
              <div className="mt-1 text-sm text-ink/55">Un chef de projet vous rappelle sous 24 h ouvrées.</div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-lime text-ink"><Clock size={18} /></span>
            <div>
              <div className="font-serif text-xl text-ink">Réponse sous 24 h ouvrées</div>
              <div className="mt-1 text-sm text-ink/55">Échange de cadrage rapide — dispositif on-site selon vos délais.</div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-lime text-ink"><Phone size={18} /></span>
            <div>
              <div className="font-serif text-xl text-ink">Contact direct</div>
              <div className="mt-1 text-sm text-ink/55">Pas de standard, pas de bot — vous parlez à un décideur.</div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
        <div className="grid gap-10 md:grid-cols-2">
          {c.body.map((p, i) => (<Reveal key={i} delay={i}><p className="text-xl leading-relaxed text-ink/70">{p}</p></Reveal>))}
        </div>
        <div className="mt-20 grid gap-px overflow-hidden rounded-2xl border border-ink/10 md:grid-cols-2 lg:grid-cols-4">
          {c.highlights.map((h, i) => (
            <Reveal key={i} delay={i}>
              <div className="h-full bg-mist p-8">
                <span className="font-serif text-2xl text-lime-dim">0{i + 1}</span>
                <h3 className="mt-4 font-serif text-2xl text-ink">{h.k}</h3>
                <p className="mt-3 text-sm text-ink/55">{h.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={1}><div className="mt-12 flex flex-wrap gap-2">{c.quartiers.map((q) => (<span key={q} className="rounded-full border border-ink/15 px-4 py-2 text-sm text-ink/60">{q}</span>))}</div></Reveal>
      </section>

      {/* Paris contact form — direct and short (decision-makers are busy) */}
      <section className="border-t border-ink/10 bg-ink text-white" data-testid="paris-form-section">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:grid-cols-12 md:px-10 md:py-28">
          <div className="md:col-span-5">
            <span className={`${overline} !text-lime`}>Direct — pas de standard</span>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-tight tracking-tight text-white md:text-5xl">
              Votre projet à Paris ?<br /><span className="italic text-lime">Réponse sous 24 h ouvrées.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/70">
              Décrivez votre besoin en 3 lignes. Un chef de projet vous rappelle sous 24 h ouvrées.
            </p>
            <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} className="mt-8 flex items-center gap-3 rounded-xl border border-lime/30 bg-white/5 p-5 transition-colors hover:border-lime hover:bg-white/10" data-testid="paris-form-phone-link">
              <Phone size={22} className="text-lime" />
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-white/50">Ou appelez directement</div>
                <div className="mt-0.5 font-serif text-2xl text-white">{CONTACT.phoneMobile}</div>
              </div>
            </a>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-2xl bg-white p-8 md:p-10">
              <SubmissionForm
                type="devis"
                testid="paris-form"
                submitLabel="Être rappelé sous 24 h ouvrées"
                fields={[
                  { name: "name", label: "Votre nom", required: true },
                  { name: "company", label: "Entreprise", required: true },
                  { name: "phone", label: "Téléphone (pour rappel)", type: "tel", required: true },
                  { name: "email", label: "Email", type: "email", required: true },
                  { name: "message", label: "Votre projet en 3 lignes", type: "textarea", full: true, required: true, placeholder: "Type d'événement, date envisagée, nombre d'invités, budget indicatif" },
                ]}
              />
            </div>
          </div>
        </div>
      </section>

      <CityProjects city={c.name} />
      <CityCTA city={c.name} />
    </>
  );
}
